import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { config, paymentConfig } from "../../config";
import metadata from "../../metadata.json";
import "../../styles/App.css";
import "../../styles/responsive.css";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "../../components/StripeComponents";
import PaymentModal from "../../components/PaymentModal";
import TermsOfService from "../../components/TermsOfService";
import { translations } from "../../translations";
import { trackScanInitiated, trackPaymentInitiated, trackFormSubmission, trackError } from "../../utils/posthogEvents";
import posthog from "posthog-js";

// Import new components
import TimeoutModal from "../../components/modals/TimeoutModal";
import RateLimitModal from "../../components/modals/RateLimitModal";
import AdditionalReportModal from "../../components/modals/AdditionalReportModal";
import NotificationModal from "../../components/modals/NotificationModal";
import SuccessModal from "../../components/modals/SuccessModal";
import PlanSelectionModal from "../../components/modals/PlanSelectionModal";
import MainForm from "../../components/forms/MainForm";
import Header from "../../components/ui/Header";

import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { identifyUserInPostHog } from "../../utils/posthogInit";

axios.defaults.baseURL = config.API_URL;

const CyberRiskReport = () => {
  const { token, email: urlEmail } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Default values if not in URL
  const oemId = searchParams.get("oemId") || "survei23a425";
  const company = searchParams.get("company") || "SurveilX";
  const scanType = searchParams.get("scanType")?.toLowerCase() || "single";
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    language: "eng",
    firstName: "",
    lastName: "",
    email: "",
    target: "",
    agree: false,
    licenseId: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [forceVerifyButton, setForceVerifyButton] = useState(false);
  const [checkMailError, setCheckMailError] = useState("");
  const [popupMessage, setPopupMessage] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(!!token);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showStripe, setShowStripe] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [clientSecret, setClientSecret] = useState("");
  const [initiatedPayment, setInitiatedPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [userId, setUserId] = useState("");
  const [userOemId, setUserOemId] = useState("");
  console.log("oemId from URL or default:", oemId);
  console.log("userOemId from state:", userOemId);
  const [plans, setPlans] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponId, setCouponId] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [discountedPlans, setDiscountedPlans] = useState([]);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponMessageType, setCouponMessageType] = useState("");
  const [originalPlansState, setOriginalPlansState] = useState([]);
  const [displayLanguage] = useState("eng");
  const [userLocation] = useState("Unknown");
  const [urlInactive, setUrlInactive] = useState(false);
  const [dynamicLogo, setDynamicLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [couponDisabled, setCouponDisabled] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [licenseKeyInvalid, setLicenseKeyInvalid] = useState(false);
  const [licenseErrorMessage, setLicenseErrorMessage] = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState(0);
  const currentLang = displayLanguage;
  const t = translations[currentLang];
  const [buttonColor, setButtonColor] = useState("#2563eb");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [colorsLoaded, setColorsLoaded] = useState(false);
  const [dbVersion, setDbVersion] = useState(null);

  // Identify user in PostHog when email is verified
  useEffect(() => {
    if (emailStatus === "verified" && formData.email) {
      identifyUserInPostHog(formData.email, userId, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        domain: cleanDomain(formData.target),
      });
    }
  }, [emailStatus, userId, formData.email]);

  const debounceRef = useRef(null);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const localVersion = `${metadata.buildMajor}.${metadata.buildMinor}.${metadata.buildRevision}`;

  const cleanDomain = (domain) => {
    return domain
      .replace(/^(www\.)?/, "")
      .split("/")[0]
      .trim();
  };

  const generateLicenseReport = async () => {
    try {
      setLoading(true);
      const cleanedDomain = cleanDomain(formData.target);

      const requestData = {
        userId: userId,
        oemId: oemId,
        licenseKey: formData.licenseId.trim(),
        domain: cleanedDomain,
        language: formData.language,
        displayLanguage: displayLanguage,
      };

      const response = await axios.post(
        "/reports/license-reports",
        requestData,
      );

      setLicenseKeyInvalid(false);
      setPopupMessage(
        response.data.message || "License scan started successfully!",
      );
      setShowPopup(true);
      const currentLanguage = formData.language;
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        target: "",
        agree: false,
        language: currentLanguage,
        licenseId: "",
      });
      setEmailStatus(null);
      setForceVerifyButton(false);
      setInitiatedPayment(false);
      setUserData(null);
    } catch (error) {
      setLicenseKeyInvalid(true);
      setLicenseErrorMessage(
        error.response?.data?.message ||
          "An error occurred during license scan",
      );
    } finally {
      setLoading(false);
    }
  };

  const [forgotLicenseModalOpen, setForgotLicenseModalOpen] = useState(false);

  const handleForgetLicenseKey = async () => {
    try {
      const response = await axios.post("/license/forgot-key", {
        userId: userId,
        oemId: oemId,
      });
      setPopupMessage(
        response.data.message || "License key sent to your email successfully",
      );
      setForgotLicenseModalOpen(true);
      setShowPopup(true);
    } catch (error) {
      setPopupMessage(
        error.response?.data?.message || "Error sending license key",
      );
      setForgotLicenseModalOpen(true);
      setShowPopup(true);
    }
  };

  useEffect(() => {
    if (!oemId) {
      setColorsLoaded(true);
    }
  }, [oemId]);

  // Add default params to URL if missing
  useEffect(() => {
    if (!searchParams.get("oemId") && !token) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("oemId", "survei23a425");
      newParams.set("company", "SurveilX");
      newParams.set("scanType", "Single");
      navigate(`/cyberRiskReport?${newParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate, token]);

  useEffect(() => {
    document.documentElement.style.setProperty("--button-color", buttonColor);
    document.documentElement.style.setProperty(
      "--background-color",
      backgroundColor,
    );
  }, [buttonColor, backgroundColor]);

  useEffect(() => {
    if (!oemId) return;
    const checkUrlStatus = async () => {
      setLogoLoading(true);
      try {
        const response = await axios.post(
          "/partners/oem-partners/check-url-status",
          { oemId: oemId },
        );
        if (response.data.status === 403) {
          setPopupMessage(response.data.response || "URL is not valid");
          setShowPopup(true);
          setUrlInactive(true);
          setUserData(null);
        } else if (response.data.status === 200) {
          if (response.data.logo) {
            let decodedLogo = response.data.logo
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, "&");
            decodedLogo = decodedLogo.replace(/^"|"$/g, "");
            setDynamicLogo(decodedLogo);
          }

          if (response.data.buttonColor) {
            setButtonColor(response.data.buttonColor);
            document.documentElement.style.setProperty(
              "--button-color",
              response.data.buttonColor,
            );
          }
          if (response.data.backgroundColor) {
            setBackgroundColor(response.data.backgroundColor);
            document.documentElement.style.setProperty(
              "--background-color",
              response.data.backgroundColor,
            );
          }
          if (response.data.appVersion) setDbVersion(response.data.appVersion);
          setColorsLoaded(true);
        }
      } catch (error) {
        console.error("Error checking URL status:", error);
        if (
          error.response?.data?.status === 403 ||
          error.response?.status === 403
        ) {
          setPopupMessage(
            error.response.data.response ||
              "This URL is not active yet. Please contact the administrator for support.",
          );
          if (error.response.data.logo)
            setDynamicLogo(error.response.data.logo);
          if (error.response.data.buttonColor) {
            setButtonColor(error.response.data.buttonColor);
            document.documentElement.style.setProperty(
              "--button-color",
              error.response.data.buttonColor,
            );
          }
          if (error.response.data.backgroundColor) {
            setBackgroundColor(error.response.data.backgroundColor);
            document.documentElement.style.setProperty(
              "--background-color",
              error.response.data.backgroundColor,
            );
          }
          setShowPopup(true);
          setUrlInactive(true);
          setUserData(null);
        } else if (error.response?.status === 404) {
          setPopupMessage("OEM Partner not found. This URL may be invalid.");
          setShowPopup(true);
          setUrlInactive(true);
          setUserData(null);
        }
      } finally {
        setLogoLoading(false);
      }
    };
    checkUrlStatus();
  }, [oemId]);

  useEffect(() => {
    const effectiveOemId = userOemId || oemId;
    if (showPlanSelection && effectiveOemId && plans.length === 0) {
      fetchPlans(effectiveOemId);
    }
  }, [showPlanSelection, userOemId]);

  useEffect(() => {
    if (showPlanSelection && plans.length > 0 && !selectedPlan) {
      const firstPlan = plans[0];
      if (firstPlan && !plansLoading) {
        setSelectedPlan(firstPlan);
        setCurrencySymbol(firstPlan.currencySymbol);
        setPaymentAmount(firstPlan.price);
      }
    }
  }, [showPlanSelection, plans, selectedPlan]);

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData((prev) => ({ ...prev, email }));
    setEmailStatus(null);
    setCheckMailError("");
    setCheckingEmail(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (
      email.length > 5 &&
      emailRegex.test(email) &&
      emailStatus !== "rate_limited"
    ) {
      setCheckingEmail(true);
      debounceRef.current = setTimeout(
        () => handleCheckEmail({ target: { value: email } }),
        700,
      );
    }
  };

  const handleEmailBlur = (e) => {
    const email = e.target.value;
    if (
      email.length > 5 &&
      emailRegex.test(email) &&
      emailStatus !== "rate_limited"
    ) {
      identifyUserInPostHog(email, userId, {
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setCheckingEmail(true);
      handleCheckEmail({ target: { value: email } });
    }
  };

  const [plansLoading, setPlansLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const fetchPlans = async (oemIdParam) => {
    setPlansLoading(true);
    try {
      const response = await axios.get(
        `/partners/oem-partners/${oemIdParam}/plans`,
      );
      if (response.data.status !== 200) {
        throw new Error(t.failedToFetchPlans);
      }
      const apiPlans = response.data.plans;
      if (!apiPlans || apiPlans.length === 0) {
        throw new Error(t.failedToFetchPlans);
      }
      let newPlans = [];

      const getSymbol = (curr) => {
        switch (curr.toUpperCase()) {
          case "USD":
            return "$";
          case "AUD":
            return "A$";
          case "EUR":
            return "€";
          case "GBP":
            return "£";
          default:
            return curr.toUpperCase();
        }
      };

      newPlans = apiPlans.map((plan, index) => ({
        id: `plan-${index}`,
        planId: plan._id,
        planPriceId: plan.prices[0]._id,
        name: plan.name,
        currencySymbol: getSymbol(plan.prices[0].currency),
        price: plan.prices[0].amount / 100,
        type:
          plan.prices[0].interval === "year" ||
          plan.name.toLowerCase().includes("year")
            ? "Annual"
            : "Monthly",
        features: plan.description ? [plan.description] : [],
        currency: plan.prices[0].currency.toUpperCase(),
      }));

      setPlans(newPlans);

      setPlansLoading(false);
      if (newPlans.length > 0) {
        setCurrencySymbol(newPlans[0].currencySymbol);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlansLoading(false);
      setPopupMessage(t.failedToFetchPlans);
      setShowPopup(true);
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      setLoading(true);

      if (!plans || plans.length === 0) {
        throw new Error(t.failedToFetchPlans);
      }

      const additionalPlan = plans[0];
      if (!additionalPlan) {
        throw new Error(t.planDataIncomplete);
      }

      // Track payment initiation
      trackPaymentInitiated({
        amount: additionalPlan.price,
        currency: additionalPlan.currency,
        planType: 'additional_report',
        email: userData?.email || formData.email,
      });

      const response = await axios.post("/billing/payment-intent", {
        amount: additionalPlan.planId ? additionalPlan.price.toString() : "10",
        currency: additionalPlan?.currency?.toLowerCase() || "usd",
        email: userData?.email || formData.email,
        domain: userData?.domain || cleanDomain(formData.target),
        planType: "Monthly",
        userId: userId,
        planId: additionalPlan.planId,
        planPriceId: additionalPlan.planPriceId,
        couponCode: appliedCoupon?.code || null,
        couponId: appliedCoupon?.couponId || null,
        flag: appliedCoupon ? "couponApplied" : null,
        oemId: oemId,
        displayLanguage: displayLanguage,
      });
      setClientSecret(response.data.clientSecret);
      setPaymentAmount(additionalPlan.price);
      setCurrencySymbol(additionalPlan.currencySymbol);
      setShowPopup(false);
      setShowStripe(true);
    } catch (error) {
      console.error("Payment initialization error:", error);
      console.error("Error response:", error.response?.data);

      // Track error
      trackError('payment_initialization_error', error.message, {
        email: userData?.email || formData.email,
        planType: 'additional_report',
      });

      let errorMessage = t.failedToInitializePayment;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setPopupMessage(errorMessage);
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEmail = (e, skipAdditionalReportModal = false) => {
    const email = e.target.value;
    if (!email || emailStatus === "rate_limited") {
      setCheckingEmail(false);
      return;
    }

    if (timeoutId) clearTimeout(timeoutId);
    setCheckingEmail(true);

    const newTimeoutId = setTimeout(async () => {
      try {
        const response = await axios.post(`/auth/check-mail`, {
          email,
          ...(oemId && { oemId }),
        });
        if (response.status === 200) {
          setCheckMailError("");
          setEmailStatus("verified");
          setForceVerifyButton(false);
          setInitiatedPayment(response.data.response.initiatedPayment || false);
          setUserId(response.data.response.userId);
          if (response.data.response.oemId) {
            setUserOemId(response.data.response.oemId);
          }
          if (!skipAdditionalReportModal) {
            setUserData({
              email: response.data.response.email,
              firstName: response.data.response.firstName,
              lastName: response.data.response.lastName,
              nextPaymentDate: response.data.response.nextPaymentDate,
              planType: response.data.response.initiatedPayment
                ? t.monthlySubscriber
                : t.annualSubscriber,
            });
          }
        }
      } catch (error) {
        if (error.response?.status === 429) {
          setEmailStatus("rate_limited");
          const retryAfter = error.response.data.retryAfter || 600;
          setRateLimitRetryAfter(retryAfter);
          setShowRateLimitModal(true);
          setForceVerifyButton(false);
          setInitiatedPayment(false);
          setUserId("");
          setUserData(null);
        } else if (error.response?.status === 404) {
          setCheckMailError("");
          setEmailStatus("unverified");
          setForceVerifyButton(true);
          setInitiatedPayment(false);
          setUserId("");
          setUserData(null);
        } else if (error.response?.status === 400) {
          setEmailStatus("org_conflict");
          setCheckMailError(
            error.response.data.message ||
              "This email is already associated with another organization. Please use a different email address to continue.",
          );
          setForceVerifyButton(false);
          setInitiatedPayment(false);
          setUserId("");
          setUserData(null);
        } else {
          setCheckMailError("");
          setEmailStatus(null);
          setForceVerifyButton(false);
        }
      } finally {
        setCheckingEmail(false);
      }
    }, 1000);

    setTimeoutId(newTimeoutId);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const verifyToken = async () => {
    try {
      setTokenLoading(true);
      const emailParam = urlEmail || formData.email || "";
      const response = await axios.get(
        `/auth/verify-token/${token}/${emailParam}`,
      );

      // Check for redirect URL (oemUrl has priority)
      const redirectUrl =
        response.data.oemUrl ||
        response.data.url ||
        response.data.response?.url;

      if (response.status === 200 && redirectUrl) {
        setIsRedirecting(true);
        const decodedUrl = redirectUrl.replace(/&amp;/g, "&");

        // Check if same domain or different domain
        const currentDomain = window.location.origin;
        const targetUrl = new URL(decodedUrl);
        const isSameDomain = currentDomain === targetUrl.origin;

        // Use MongoDB _id from response for plans API; URL oemId is only for check-url-status
        const responseOemId = response.data.response?.oemId || "";

        if (isSameDomain) {
          // Same domain: use sessionStorage
          sessionStorage.setItem(
            "verifiedUserData",
            JSON.stringify({
              language: response.data.response?.language || "eng",
              firstName: response.data.response?.firstName || "",
              lastName: response.data.response?.lastName || "",
              email: response.data.response?.email || "",
              target: response.data.response?.domain || "",
              userId: response.data.response?._id || "",
              oemId: responseOemId,
              initiatedPayment:
                response.data.response?.initiatedPayment || false,
            }),
          );
        } else {
          // Cross-domain: add data to URL params
          targetUrl.searchParams.set("verified", "true");
          targetUrl.searchParams.set(
            "firstName",
            response.data.response?.firstName || "",
          );
          targetUrl.searchParams.set(
            "lastName",
            response.data.response?.lastName || "",
          );
          targetUrl.searchParams.set(
            "email",
            response.data.response?.email || "",
          );
          targetUrl.searchParams.set(
            "domain",
            response.data.response?.domain || "",
          );
          targetUrl.searchParams.set(
            "userId",
            response.data.response?._id || "",
          );
          targetUrl.searchParams.set("userOemId", responseOemId);
          targetUrl.searchParams.set(
            "initiatedPayment",
            response.data.response?.initiatedPayment || "false",
          );
        }

        setTimeout(() => {
          window.location.href = isSameDomain
            ? decodedUrl
            : targetUrl.toString();
        }, 300);
        return;
      }

      if (response.data.status !== 200) {
        throw new Error(response.data.message || t.invalidToken);
      }

      // No redirect, pre-fill form on current page
      const tokenLanguage = response?.data?.response?.language || "eng";
      setFormData({
        language: tokenLanguage,
        firstName: response.data.response.firstName || "",
        lastName: response.data.response.lastName || "",
        email: response.data.response.email || "",
        target: response.data.response.domain || "",
        agree: true,
        licenseId: "",
      });
      setEmailStatus("verified");
      setTokenVerified(true);
      setInitiatedPayment(response.data.response.initiatedPayment || false);
      setUserId(response.data.response._id || "");
      if (response.data.response.oemId) {
        setUserOemId(response.data.response.oemId);
      } else {
        setUserOemId(oemId);
      }
      const message = translations.eng.emailVerifiedSuccess;
      setPopupMessage(message);
      setShowPopup(true);
    } catch (error) {
      // Handle error response (400, 404, etc.)
      const errorMessage =
        error.response?.data?.message || t.invalidOrExpiredVerificationToken;
      const oemUrl = error.response?.data?.oemUrl;

      if (oemUrl) {
        setIsRedirecting(true);
        const decodedUrl = oemUrl.replace(/&amp;/g, "&");
        const targetUrl = new URL(decodedUrl);
        const currentDomain = window.location.origin;
        const isSameDomain = currentDomain === targetUrl.origin;

        if (isSameDomain) {
          // Same domain: use sessionStorage
          sessionStorage.setItem(
            "verificationError",
            JSON.stringify({
              message: errorMessage,
            }),
          );
        } else {
          // Cross-domain: add error to URL params
          targetUrl.searchParams.set("verificationError", "true");
          targetUrl.searchParams.set("errorMessage", errorMessage);
        }

        setTimeout(() => {
          window.location.href = isSameDomain
            ? decodedUrl
            : targetUrl.toString();
        }, 300);
        return;
      }

      // No oemUrl, show error on current page
      setPopupMessage(errorMessage);
      setShowPopup(true);
      setInitiatedPayment(false);
      setUserId("");
      navigate("/cyberRiskReport", { replace: true });
    } finally {
      setTokenLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      // Check URL params first (for cross-domain)
      const verified = searchParams.get("verified");
      const verificationError = searchParams.get("verificationError");

      if (verificationError === "true") {
        // Show error, don't show form
        const errorMessage =
          searchParams.get("errorMessage") ||
          t.invalidOrExpiredVerificationToken;
        setPopupMessage(errorMessage);
        setShowPopup(true);
        setUrlInactive(true); // Hide the form
        // Clean URL
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("verificationError");
        cleanUrl.searchParams.delete("errorMessage");
        window.history.replaceState({}, "", cleanUrl.toString());
        return;
      }

      if (verified === "true") {
        // Pre-fill from URL params (cross-domain)
        setFormData({
          language: "eng",
          firstName: searchParams.get("firstName") || "",
          lastName: searchParams.get("lastName") || "",
          email: searchParams.get("email") || "",
          target: searchParams.get("domain") || "",
          agree: true,
          licenseId: "",
        });
        setEmailStatus("verified");
        setUserId(searchParams.get("userId") || "");
        setUserOemId(searchParams.get("userOemId") || "");
        setInitiatedPayment(searchParams.get("initiatedPayment") === "true");
        // Clean URL
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete("verified");
        cleanUrl.searchParams.delete("firstName");
        cleanUrl.searchParams.delete("lastName");
        cleanUrl.searchParams.delete("email");
        cleanUrl.searchParams.delete("domain");
        cleanUrl.searchParams.delete("userId");
        cleanUrl.searchParams.delete("userOemId");
        cleanUrl.searchParams.delete("initiatedPayment");
        window.history.replaceState({}, "", cleanUrl.toString());
        return;
      }

      // Check sessionStorage (for same-domain)
      const verificationErrorStorage =
        sessionStorage.getItem("verificationError");
      if (verificationErrorStorage) {
        try {
          const errorData = JSON.parse(verificationErrorStorage);
          setPopupMessage(errorData.message);
          setShowPopup(true);
          setUrlInactive(true); // Hide the form
          sessionStorage.removeItem("verificationError");
        } catch (error) {
          console.error("Error parsing verification error:", error);
        }
        return;
      }

      const storedData = sessionStorage.getItem("verifiedUserData");
      if (storedData) {
        try {
          const userData = JSON.parse(storedData);
          setFormData({
            language: userData.language || "eng",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "",
            target: userData.target || "",
            agree: true,
            licenseId: "",
          });
          setEmailStatus("verified");
          setUserId(userData.userId || "");
          setUserOemId(userData.oemId || oemId);
          setInitiatedPayment(userData.initiatedPayment || false);
          sessionStorage.removeItem("verifiedUserData");
        } catch (error) {
          console.error("Error parsing stored user data:", error);
        }
      }
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = name === "licenseId" ? value.trim() : value;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : processedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === "licenseId") {
      setLicenseKeyInvalid(false);
      setLicenseErrorMessage("");
    }

    if (name === "email" || name === "target") {
      if (userData) {
        setUserData(null);
      }
      if (name === "email") {
        setUserOemId(oemId);
        setPlans([]);
        setCheckMailError("");
        setCheckingEmail(false);
      }
    }
  };

  const validateEmailDomain = () => {
    try {
      const emailDomain = formData.email.split("@")[1]?.toLowerCase();
      const cleanedTarget = cleanDomain(formData.target).toLowerCase();
      return (
        emailDomain === cleanedTarget ||
        cleanedTarget.startsWith(emailDomain + ".") ||
        emailDomain.startsWith(cleanedTarget + ".")
      );
    } catch (e) {
      return false;
    }
  };

  const isFormValid = () => {
    const baseValid =
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.email.trim() &&
      formData.target.trim() &&
      formData.agree &&
      validateEmailDomain() &&
      !checkMailError &&
      !checkingEmail &&
      !Object.keys(errors).length;

    if (scanType === "license" && emailStatus === "verified") {
      return baseValid && formData.licenseId.trim();
    }

    return baseValid;
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim()) newErrors.firstName = t.firstNameRequired;
    if (!formData.lastName.trim()) newErrors.lastName = t.lastNameRequired;
    if (!formData.email.trim()) newErrors.email = t.emailRequired;
    else if (!emailRegex.test(formData.email)) newErrors.email = t.emailInvalid;
    if (!formData.target.trim()) newErrors.target = t.domainRequired;
    else if (
      !/^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i.test(
        cleanDomain(formData.target),
      )
    ) {
      newErrors.target = t.domainInvalid;
    }
    if (
      scanType === "license" &&
      emailStatus === "verified" &&
      !formData.licenseId.trim()
    ) {
      newErrors.licenseId = "License ID is required";
    }
    if (!formData.agree) newErrors.agree = t.termsRequired;

    if (
      !newErrors.email &&
      !newErrors.target &&
      formData.email &&
      formData.target
    ) {
      if (!validateEmailDomain()) {
        newErrors.target = t.emailDomainMismatch;
        newErrors.email = t.emailDomainMismatch;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (paymentData?.response) {
      setFormData((prev) => ({
        ...prev,
        firstName: paymentData.response.firstName || "",
        lastName: paymentData.response.lastName || "",
        email: paymentData.response.email || "",
        target: paymentData.response.domain || "",
        agree: true,
        language: prev.language || "eng",
      }));
    }
  }, [paymentData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Identify user in PostHog immediately whenever submit is clicked
    if (formData.email && emailRegex.test(formData.email)) {
      identifyUserInPostHog(formData.email, userId, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        domain: cleanDomain(formData.target),
      });
    }

    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const cleanedDomain = cleanDomain(formData.target);
      
      // Track form submission
      trackFormSubmission('cyber_risk_report', {
        scanType: scanType,
        email: formData.email,
        domain: cleanedDomain,
      });
      
      if (forceVerifyButton) {
        const response = await axios.post("/auth/verify-mail", {
          email: formData.email,
          target: cleanedDomain,
          firstName: formData.firstName,
          lastName: formData.lastName,
          language: formData.language,
          displayLanguage: displayLanguage,
          ...(oemId && { oemId }),
        });
        setPopupMessage(response.data.message || t.verificationEmailSent);
        setShowPopup(true);
        setEmailStatus(null);
        setForceVerifyButton(false);
        const currentLanguage = formData.language;
        setFormData({
          ...formData,
          firstName: "",
          lastName: "",
          email: "",
          target: "",
          agree: false,
          language: currentLanguage,
          licenseId: "",
        });
        setCurrencySymbol("$");
        setCouponCode("");
        setAppliedCoupon(null);
        setDiscountedPlans([]);
        setOriginalPlansState([]);
        setCouponMessage("");
        setCouponMessageType("");
        return;
      }

      if (emailStatus === "verified" && !initiatedPayment) {
        if (scanType === "license") {
          await generateLicenseReport();
          trackScanInitiated({
            scanType: 'license',
            domain: cleanedDomain,
            email: formData.email,
          });
          return;
        }
        setShowPlanSelection(true);
        trackScanInitiated({
          scanType: scanType,
          domain: cleanedDomain,
          email: formData.email,
        });
        return;
      }

      if (emailStatus === "verified" && initiatedPayment) {
        if (!plans || plans.length === 0) {
          await fetchPlans(userOemId || oemId);
        }
        if (!userData) {
          try {
            const response = await axios.post(`/auth/check-mail`, {
              email: formData.email,
              ...(oemId && { oemId }),
            });
            if (response.status === 200) {
              setUserData({
                email: response.data.response.email,
                firstName: response.data.response.firstName,
                lastName: response.data.response.lastName,
                nextPaymentDate: response.data.response.nextPaymentDate,
                planType: response.data.response.initiatedPayment
                  ? t.monthlySubscriber
                  : t.annualSubscriber,
              });
            }
          } catch (error) {
            console.error("Error fetching user data:", error);
          }
        }
        console.log("Showing modal with userData:", userData);
        setShowPopup(true);
        return;
      }

      const requestData = {
        language: formData.language,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        oemId: oemId || userOemId || null,
        target: cleanedDomain,
        appVersion: dbVersion || localVersion,
      };
      const response = await axios.post("/reports/cyber-reports", requestData);
      if (response.data.response) {
        setUserData({
          firstName: response.data.response.firstName,
          lastName: response.data.response.lastName,
          email: response.data.response.email,
          domain: response.data.response.domain,
          displayLanguage: displayLanguage,
          planType: response.data.message
            ? t.annualSubscriber
            : t.annualSubscriber,
        });
      }
      if (response.data.flag === "Request Additional Report") {
        setShowPopup(true);
      } else {
        setPopupMessage(response.data.message || t.assessmentStarted);
        setShowPopup(true);
      }
      if (
        !response.data.flag ||
        response.data.flag !== "Request Additional Report"
      ) {
        setFormData({
          ...formData,
          firstName: "",
          lastName: "",
          email: "",
          target: "",
          agree: false,
          language: formData.language,
          licenseId: "",
        });
        setEmailStatus(null);
        setForceVerifyButton(false);
        setInitiatedPayment(false);
        setUserData(null);
        setCouponCode("");
        setAppliedCoupon(null);
        setDiscountedPlans([]);
        setOriginalPlansState([]);
        setCouponMessage("");
        setCouponMessageType("");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        t.anErrorOccurred;
      setPopupMessage(errorMessage);
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const closePopup = () => {
    setShowTimeoutPopup(false);
    if (timeoutId) clearTimeout(timeoutId);
  };

  const handlePlanSelect = async (plan) => {
    if (appliedCoupon && selectedPlan?.id !== plan.id) {
      try {
        setCouponLoading(true);
        setCouponMessage("");

        const response = await axios.post("/billing/coupons/apply", {
          userId: userId,
          couponCode: appliedCoupon.code,
          amount: plan.price.toString(),
          currency: plan?.currency?.toLowerCase() || "usd",
          planId: plan.planId,
          language: formData.language,
          displayLanguage: displayLanguage,
        });

        if (response.data.status === 200) {
          const discountedAmount = response.data.displayDiscountedAmount || 0;
          setAppliedCoupon({
            code: appliedCoupon.code,
            discountedAmount: discountedAmount,
            couponId: response?.data?.coupon?.couponId,
            promotionCodeId: response?.data?.coupon?.promoCodeId,
          });

          const resetPlans = plans.map((p) => ({
            ...p,
            price: p.originalPrice || p.price,
            originalPrice: undefined,
            discounted: false,
          }));

          const updatedPlans = resetPlans.map((p) => {
            if (p.id === plan.id) {
              return {
                ...p,
                originalPrice: p.price,
                price: Math.max(0, discountedAmount),
                discounted: true,
              };
            }
            return p;
          });

          setDiscountedPlans(updatedPlans);

          const updatedSelectedPlan = updatedPlans.find(
            (p) => p.id === plan.id,
          );
          setSelectedPlan(updatedSelectedPlan);
          setCurrencySymbol(updatedSelectedPlan.currencySymbol);
          setPaymentAmount(updatedSelectedPlan.price);

          setCouponMessage(
            `${t.couponApplied} "${appliedCoupon.code}" ${t.discountApplied}!`,
          );
          setCouponMessageType("success");
        }
      } catch (error) {
        setCouponMessage(
          error.response?.data?.message || t.couponInvalidForPlan,
        );
        setCouponMessageType("error");
        setDiscountedPlans([]);
        setAppliedCoupon(null);
        setSelectedPlan(plan);
        setCurrencySymbol(plan.currencySymbol);
        setPaymentAmount(plan.price);
      } finally {
        setCouponLoading(false);
      }
    } else {
      setSelectedPlan(plan);
      setCurrencySymbol(plan.currencySymbol);
      setPaymentAmount(plan.price);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage(t.couponCodePlaceholder);
      setCouponMessageType("error");
      return;
    }

    if (!selectedPlan) {
      setCouponMessage(t.planDataIncomplete);
      setCouponMessageType("error");
      return;
    }

    setCouponLoading(true);
    setCouponMessage("");

    if (originalPlansState.length === 0) {
      setOriginalPlansState([...plans]);
    }

    try {
      const response = await axios.post("/billing/coupons/apply", {
        userId: userId,
        couponCode: couponCode.trim(),
        amount: selectedPlan.price.toString(),
        currency: selectedPlan?.currency?.toLowerCase() || "usd",
        planId: selectedPlan.planId,
        language: formData.language,
        displayLanguage: displayLanguage,
      });
      if (response.data.status === 200) {
        const discountedAmount = response.data.displayDiscountedAmount || 0;
        setAppliedCoupon({
          code: couponCode.trim(),
          discountedAmount: discountedAmount,
          couponId: response?.data?.coupon?.couponId,
          promotionCodeId: response?.data?.coupon?.promoCodeId,
        });

        const updatedPlans = plans.map((plan) => {
          if (plan.id === selectedPlan.id) {
            return {
              ...plan,
              originalPrice: plan.price,
              price: Math.max(0, discountedAmount),
              discounted: true,
            };
          }
          return plan;
        });
        setDiscountedPlans(updatedPlans);

        const updatedSelectedPlan = updatedPlans.find(
          (p) => p.id === selectedPlan.id,
        );
        setSelectedPlan(updatedSelectedPlan);
        setPaymentAmount(updatedSelectedPlan.price);

        setCouponMessage(t.couponApplied);
        setCouponMessageType("success");
      }
    } catch (error) {
      setCouponMessage(error.response?.data?.message || t.couponInvalid);
      setCouponMessageType("error");
    } finally {
      setCouponLoading(false);
    }
  };

  const handlePlanSelection = async () => {
    const cleanedDomain = cleanDomain(formData.target);
    const finalPrice = selectedPlan?.price || 0;
    setPaymentLoading(true);
    try {
      if (finalPrice === 0 && appliedCoupon) {
        const response = await axios.post("/billing/payment-intent", {
          amount: finalPrice,
          currency: selectedPlan?.currency?.toLowerCase() || "usd",
          email: formData.email,
          domain: cleanedDomain,
          planType: selectedPlan.type,
          userId: userId,
          planId: selectedPlan.planId,
          oemId: oemId,
          planPriceId: selectedPlan.planPriceId,
          couponCode: appliedCoupon?.code || null,
          couponId: appliedCoupon?.couponId || null,
          promotionCodeId: appliedCoupon?.promotionCodeId || null,
          flag: appliedCoupon ? "couponApplied" : null,
          displayLanguage: displayLanguage,
        });
        setPaymentData({
          ...response.data,
          paymentIntentId: String(response.data.paymentIntentId),
        });
        setShowPlanSelection(false);
        setShowSuccessModal(true);
        setSuccessMessage(
          response.data.message ||
            `${t.couponApplied} "${appliedCoupon?.code}" ${t.discountApplied}!`,
        );
        return;
      }

      if (!selectedPlan?.planId || !selectedPlan?.planPriceId) {
        throw new Error(t.selectedPlanDataIncomplete);
      }

      setPaymentAmount(finalPrice);
      setCurrencySymbol(selectedPlan?.currencySymbol);

      const response = await axios.post("/billing/payment-intent", {
        amount: finalPrice,
        currency: selectedPlan?.currency?.toLowerCase() || "usd",
        email: formData.email,
        domain: cleanedDomain,
        planType: selectedPlan.type,
        userId: userId,
        planId: selectedPlan.planId,
        oemId: oemId,
        planPriceId: selectedPlan.planPriceId,
        couponCode: appliedCoupon?.code || null,
        couponId: appliedCoupon?.couponId || null,
        promotionCodeId: appliedCoupon?.promotionCodeId || null,
        flag: appliedCoupon ? "couponApplied" : null,
        displayLanguage: displayLanguage,
      });

      if (response.data.status === 200) {
        setClientSecret(response.data.clientSecret);
        setShowPlanSelection(false);
        setShowStripe(true);
      }
    } catch (error) {
      setPopupMessage(
        error.response?.data?.message || t.failedToInitializePayment,
      );
      setShowPopup(true);
      setShowPlanSelection(false);
    } finally {
      setPaymentLoading(false);
    }
  };

  if (tokenLoading) {
    return <LoadingSpinner translations={t} />;
  }

  if (oemId && logoLoading && !colorsLoaded) {
    return <LoadingSpinner translations={t} />;
  }

  return (
    <div
      className="position-relative"
      style={{
        backgroundColor: backgroundColor,
        minHeight: "100vh",
        transition: "background-color 0.3s ease",
      }}
    >
      {/* Redirect Overlay */}
      {isRedirecting && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: backgroundColor,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            opacity: 1,
            transition: "opacity 0.3s ease",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              className="spinner-border"
              style={{
                width: "50px",
                height: "50px",
                color: buttonColor,
                borderWidth: "4px",
              }}
              role="status"
            >
              <span className="visually-hidden">Redirecting...</span>
            </div>
            <p
              style={{
                marginTop: "20px",
                color: buttonColor,
                fontSize: "16px",
              }}
            >
              Redirecting...
            </p>
          </div>
        </div>
      )}

      <TimeoutModal
        show={showTimeoutPopup}
        onClose={closePopup}
        translations={t}
        email={formData.email}
      />

      <RateLimitModal
        show={showRateLimitModal}
        onClose={() => setShowRateLimitModal(false)}
        translations={t}
        retryAfter={rateLimitRetryAfter}
        email={formData.email}
      />

      <AdditionalReportModal
        show={showPopup && userData && !forceVerifyButton}
        onClose={() => {
          setShowPopup(false);
          setUserData(null);
        }}
        userData={userData}
        translations={t}
        currencySymbol={currencySymbol}
        plans={plans}
        loading={loading}
        plansLoading={plansLoading}
        onPurchase={handlePurchase}
      />

      <NotificationModal
        show={!!licenseErrorMessage}
        onClose={() => setLicenseErrorMessage("")}
        message={licenseErrorMessage}
        translations={t}
        urlInactive={false}
        dynamicLogo={dynamicLogo}
        onClearCoupon={() => {}}
      />

      <NotificationModal
        show={showPopup && !userData}
        onClose={
          urlInactive
            ? () => {}
            : () => {
                setShowPopup(false);
                if (
                  forgotLicenseModalOpen &&
                  formData.email &&
                  emailRegex.test(formData.email)
                ) {
                  handleCheckEmail({ target: { value: formData.email } }, true);
                }
                setForgotLicenseModalOpen(false);
                const currentParams = new URLSearchParams(
                  window.location.search,
                );
                if (
                  currentParams.toString() &&
                  !window.location.search.includes(currentParams.toString())
                ) {
                  navigate(`/cyberRiskReport?${currentParams.toString()}`, {
                    replace: true,
                  });
                }
              }
        }
        message={popupMessage}
        translations={t}
        urlInactive={urlInactive}
        dynamicLogo={dynamicLogo}
        onClearCoupon={() => {
          setCouponCode("");
          setAppliedCoupon(null);
          setDiscountedPlans([]);
          setOriginalPlansState([]);
          setCouponMessage("");
          setCouponMessageType("");
          setEmailStatus(null);
        }}
      />

      <PaymentModal
        showStripe={showStripe}
        setShowStripe={setShowStripe}
        processing={processing}
        setProcessing={setProcessing}
        clientSecret={clientSecret}
        language={formData.language}
        displayLanguage={displayLanguage}
        onSuccess={() => {
          setShowStripe(false);
          setInitiatedPayment(true);
          setShowSuccessModal(true);
        }}
        onCancel={async () => {
          setShowStripe(false);
          setForceVerifyButton(false);
          setInitiatedPayment(false);
          setSelectedPlan(null);
          setCurrencySymbol("$");
          setClientSecret("");
          setUserData(null);
          setPaymentData(null);
          setCouponCode("");
          setAppliedCoupon(null);
          setDiscountedPlans([]);
          setOriginalPlansState([]);
          setCouponMessage("");
          setCouponMessageType("");
          setCouponDisabled(false);
          if (formData.email) {
            try {
              const response = await axios.post(`/auth/check-mail`, {
                email: formData.email,
                ...(oemId && { oemId }),
              });
              if (response.status === 200) {
                setEmailStatus("verified");
                setInitiatedPayment(
                  response.data.response.initiatedPayment || false,
                );
                setUserId(response.data.response.userId);
                if (response.data.response.oemId)
                  setUserOemId(response.data.response.oemId);
                setUserData({
                  email: response.data.response.email,
                  firstName: response.data.response.firstName,
                  lastName: response.data.response.lastName,
                  nextPaymentDate: response.data.response.nextPaymentDate,
                  planType: response.data.response.initiatedPayment
                    ? t.monthlySubscriber
                    : t.annualSubscriber,
                });
              }
            } catch {
              setEmailStatus(null);
            }
          }
          const currentParams = new URLSearchParams(window.location.search);
          navigate(
            `/cyberRiskReport${currentParams.toString() ? `?${currentParams.toString()}` : ""}`,
            { replace: true },
          );
        }}
        setPaymentData={setPaymentData}
        paymentAmount={paymentAmount}
        currency="usd"
        currencySymbol={currencySymbol}
        translations={{
          completePayment: t.completePayment,
          processing: t.processing,
          cancel: t.cancel,
          pay: t.pay,
        }}
      />

      <SuccessModal
        show={showSuccessModal}
        onClose={async () => {
          setShowSuccessModal(false);
          setUserData(null);
          if (successMessage || paymentData) {
            try {
              setReportLoading(true);
              const cleanedDomain = cleanDomain(formData.target);
              const requestData = {
                language: formData.language,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                target: cleanedDomain,
                displayLanguage: displayLanguage,
                oemId: oemId || userOemId || null,
                appVersion: dbVersion || localVersion,
                paymentIntentId: paymentData?.paymentIntentId || null,
              };
              const response = await axios.post(
                "/reports/cyber-reports",
                requestData,
              );
              if (
                !successMessage ||
                !successMessage.includes(t.couponApplied)
              ) {
                setPopupMessage(response.data.message || "Assessment started!");
                setShowPopup(true);
              }
              const currentLanguage = formData.language;
              setFormData({
                firstName: "",
                lastName: "",
                email: "",
                target: "",
                agree: false,
                language: currentLanguage,
                licenseId: "",
              });
              setEmailStatus("verified");
              setForceVerifyButton(false);
              setInitiatedPayment(true);
              setCurrencySymbol("$");
              setUserId("");
              setPaymentData(null);
              setSelectedPlan(null);
              setClientSecret("");
            } catch (error) {
              setPopupMessage(
                error.response?.data?.message || t.anErrorOccurred,
              );
              setShowPopup(true);
            } finally {
              setReportLoading(false);
            }
          }
          setCouponCode("");
          setAppliedCoupon(null);
          setDiscountedPlans([]);
          setOriginalPlansState([]);
          setSuccessMessage("");
          const currentParams = new URLSearchParams(window.location.search);
          navigate(
            `/cyberRiskReport${currentParams.toString() ? `?${currentParams.toString()}` : ""}`,
            { replace: true },
          );
        }}
        successMessage={successMessage}
        paymentData={paymentData}
        translations={t}
      />

      <PlanSelectionModal
        show={showPlanSelection}
        onClose={() => {
          setShowPlanSelection(false);
          setSelectedPlan(null);
          setCouponCode("");
          setCouponMessage("");
          setAppliedCoupon(null);
          setDiscountedPlans([]);
          setOriginalPlansState([]);
          setPaymentLoading(false);
          setCouponDisabled(false);
        }}
        plans={plans}
        discountedPlans={discountedPlans}
        selectedPlan={selectedPlan}
        onPlanSelect={handlePlanSelect}
        translations={t}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        appliedCoupon={appliedCoupon}
        couponLoading={couponLoading}
        couponMessage={couponMessage}
        couponMessageType={couponMessageType}
        onApplyCoupon={applyCoupon}
        couponDisabled={couponDisabled}
        onRemoveCoupon={() => {
          setDiscountedPlans([]);
          setPlans(originalPlansState.length > 0 ? originalPlansState : plans);
          setSelectedPlan(
            originalPlansState.length > 0
              ? originalPlansState.find((p) => p.id === selectedPlan?.id)
              : selectedPlan,
          );
          setPaymentAmount(
            originalPlansState.length > 0
              ? originalPlansState.find((p) => p.id === selectedPlan?.id)
                  ?.price || 0
              : selectedPlan?.originalPrice || selectedPlan?.price || 0,
          );
          setAppliedCoupon(null);
          setCouponCode("");
          setCouponMessage("");
          setCouponMessageType("");
        }}
        onPayNow={handlePlanSelection}
        plansLoading={plansLoading}
        paymentLoading={paymentLoading}
      />

      {reportLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "500px",
              height: "280px",
              padding: "24px 20px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  backgroundColor: "#10b981",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 15px",
                  position: "relative",
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 12L11 14L15 10"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="white"
                    strokeWidth="1.5"
                  />
                </svg>
                <div
                  className="spinner-border"
                  style={{
                    position: "absolute",
                    width: "70px",
                    height: "70px",
                    color: "#10b981",
                    borderWidth: "3px",
                  }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
            <h5
              style={{
                color: "#1f2937",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              {t.confirmingPayment || "Confirming Payment"}
            </h5>
            <p style={{ color: "#6b7280", marginBottom: 0, fontSize: "14px" }}>
              {t.paymentProcessingMessage ||
                "Please wait while we confirm your payment and process your request..."}
            </p>
          </div>
        </div>
      )}

      <div
        style={{
          filter:
            showTimeoutPopup ||
            showRateLimitModal ||
            showPopup ||
            showStripe ||
            showPlanSelection ||
            showSuccessModal
              ? "blur(2px)"
              : "none",
          padding: "10px",
        }}
      >
        <Header translations={t} logo={dynamicLogo} logoLoading={logoLoading} />
        <MainForm
          formData={formData}
          errors={errors}
          loading={loading}
          emailStatus={emailStatus}
          forceVerifyButton={forceVerifyButton}
          initiatedPayment={initiatedPayment}
          translations={t}
          onSubmit={handleSubmit}
          onChange={handleChange}
          onCheckEmail={handleCheckEmail}
          onEmailChange={handleEmailChange}
          onEmailBlur={handleEmailBlur}
          isFormValid={isFormValid}
          checkMailError={checkMailError}
          scanType={scanType}
          handleForgetLicenseKey={handleForgetLicenseKey}
          licenseKeyInvalid={licenseKeyInvalid}
          checkingEmail={checkingEmail}
          buttonColor={buttonColor}
          backgroundColor={backgroundColor}
          dbVersion={dbVersion}
        />
      </div>
    </div>
  );
};

export default CyberRiskReport;
